import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    FpModal, FpButton, RarityPill, HabboTag, TextInput, Select, RadioRow, Field, EmptyState, useFigureImage,
} from './frankopopShared';
import {
    useFrankopopData, fetchMyPops, fetchSets, fetchSet, fetchListing,
    createListing, acceptListing, makeOffer, relativeTime, PACKAGING_LABELS, SCRAP_UNIT,
} from '../../data/frankopop';

// ─────────────────────────────────────────────────────────────────────────────
// Pop tile - one FrankoPop (an owned copy, or a "want" with a packaging rule)
// ─────────────────────────────────────────────────────────────────────────────
function PackagingTag({ label, tone = 'gray' }) {
    const tones = {
        gray: 'bg-gray-200 text-gray-700',
        amber: 'bg-amber-100 text-amber-800',
        blue: 'bg-sky-100 text-sky-800',
    };
    return (
        <span className={`inline-block font-['Jersey_10'] uppercase text-[10px] tracking-[1px] px-1.5 py-0.5 ${tones[tone]}`}>
            {label}
        </span>
    );
}

// `pop` is an owned copy ({ serialNumber, inCase, setName, figure }). For a want,
// pass `figure` + `packaging` instead.
export function PopTile({ pop, figure: wantFigure, packaging, selected = false, onClick, disabled = false }) {
    const figure = pop ? pop.figure : wantFigure;
    const sealed = pop ? pop.inCase : packaging === 'boxed';
    const src = useFigureImage(figure, sealed);
    const setName = pop ? pop.setName : wantFigure?.setName;
    const clickable = !!onClick && !disabled;

    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            className={`relative bg-white border flex flex-col items-center text-center p-2 w-[128px] ${
                selected ? 'border-maroon ring-2 ring-maroon' : 'border-gray-300'
            } ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${disabled ? 'opacity-40' : ''}`}
            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.12)' }}
        >
            {selected && (
                <span className="absolute top-1 right-1 bg-maroon text-white text-[11px] leading-none px-1.5 py-1 font-bold">✓</span>
            )}
            <div className="flex items-center justify-center min-h-[64px]">
                {src && <img src={src} alt={figure?.name || ''} />}
            </div>
            <p className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-900 leading-tight mt-1 break-words w-full">
                {figure?.name || 'Unknown'}
            </p>
            {setName && <p className="text-[10px] text-gray-400 leading-tight w-full truncate" title={setName}>{setName}</p>}
            <div className="flex flex-wrap justify-center gap-1 mt-1">
                <RarityPill name={figure?.rarityName} colour={figure?.rarityColour} />
            </div>
            <div className="flex flex-wrap justify-center gap-1 mt-1">
                {pop ? (
                    <>
                        <PackagingTag label={`#${pop.serialNumber}`} />
                        <PackagingTag label={pop.inCase ? 'Boxed' : 'Unboxed'} tone={pop.inCase ? 'amber' : 'blue'} />
                    </>
                ) : (
                    <PackagingTag label={PACKAGING_LABELS[packaging] || 'Either'} tone={packaging === 'boxed' ? 'amber' : packaging === 'unboxed' ? 'blue' : 'gray'} />
                )}
            </div>
        </div>
    );
}

function TileRow({ children, empty }) {
    const items = React.Children.toArray(children);
    if (!items.length) return <p className="text-sm text-gray-400 italic">{empty}</p>;
    return <div className="flex flex-wrap gap-2">{items}</div>;
}

const SectionLabel = ({ children }) => (
    <p className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-500 mb-1.5">{children}</p>
);

// ─────────────────────────────────────────────────────────────────────────────
// Listing card
// ─────────────────────────────────────────────────────────────────────────────
export function ListingCard({ listing, loggedIn, onTrade, onOffer, onWithdrawOffer, onCancel, showStatus = false }) {
    const closed = listing.status !== 'active';
    let actions = null;

    if (!closed) {
        if (listing.isMine) {
            actions = (
                <>
                    <span className="text-xs text-gray-500">
                        Your listing · {listing.pendingOffers} pending offer{listing.pendingOffers === 1 ? '' : 's'}
                    </span>
                    {onCancel && <FpButton variant="ghost" size="sm" onClick={() => onCancel(listing)}>Cancel listing</FpButton>}
                </>
            );
        } else if (!loggedIn) {
            actions = <span className="text-xs text-gray-500">Log in to trade or make an offer.</span>;
        } else {
            actions = (
                <>
                    {listing.canAccept && <FpButton variant="primary" size="sm" onClick={() => onTrade(listing)}>Trade</FpButton>}
                    {listing.myOfferId ? (
                        <>
                            <span className="text-xs text-gray-500">You have an offer pending</span>
                            {onWithdrawOffer && <FpButton variant="ghost" size="sm" onClick={() => onWithdrawOffer(listing.myOfferId)}>Withdraw offer</FpButton>}
                        </>
                    ) : (
                        <FpButton variant={listing.canAccept ? 'ghost' : 'primary'} size="sm" onClick={() => onOffer(listing)}>Make offer</FpButton>
                    )}
                </>
            );
        }
    }

    return (
        <div className="bg-white border border-gray-300" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}>
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200 bg-gray-50">
                <HabboTag user={listing.seller} sub={`Listed ${relativeTime(listing.createdAt)}`} />
                {showStatus && <TradeStatusPill status={listing.status} />}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 p-3 items-start">
                <div>
                    <SectionLabel>Up for trade</SectionLabel>
                    <TileRow empty="Nothing listed.">
                        {listing.items.map((pop) => <PopTile key={pop.id} pop={pop} />)}
                    </TileRow>
                </div>
                <div className="hidden md:flex items-center self-center text-2xl text-gray-300 px-1" aria-hidden>⇄</div>
                <div>
                    <SectionLabel>Wants</SectionLabel>
                    <TileRow empty="Open to offers - nothing specific asked for.">
                        {listing.wants.map((w) => <PopTile key={w.id} figure={w.figure} packaging={w.packaging} />)}
                    </TileRow>
                </div>
            </div>
            {listing.note && (
                <p className="px-3 pb-2 text-sm text-gray-600 italic break-words">&ldquo;{listing.note}&rdquo;</p>
            )}
            {actions && (
                <div className="flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-t border-gray-200">{actions}</div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status pills
// ─────────────────────────────────────────────────────────────────────────────
const TRADE_STATUS_META = {
    active: { label: 'Active', colour: '#16a34a' },
    completed: { label: 'Traded', colour: '#2563eb' },
    cancelled: { label: 'Cancelled', colour: '#6b7280' },
    pending: { label: 'Pending', colour: '#c47f17' },
    accepted: { label: 'Accepted', colour: '#16a34a' },
    declined: { label: 'Declined', colour: '#b91c1c' },
    withdrawn: { label: 'Withdrawn', colour: '#6b7280' },
    closed: { label: 'Listing closed', colour: '#6b7280' },
    unavailable: { label: 'No longer valid', colour: '#6b7280' },
};

export function TradeStatusPill({ status }) {
    const meta = TRADE_STATUS_META[status] || { label: status, colour: '#6b7280' };
    return (
        <span className="inline-block font-['Jersey_10'] uppercase text-[10px] tracking-[1px] text-white px-1.5 py-0.5 shrink-0" style={{ backgroundColor: meta.colour }}>
            {meta.label}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Offer card (received or sent)
// ─────────────────────────────────────────────────────────────────────────────
export function OfferCard({ offer, mode, onAccept, onDecline, onWithdraw }) {
    const received = mode === 'received';
    const pending = offer.status === 'pending';
    return (
        <div className="bg-white border border-gray-300" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}>
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200 bg-gray-50">
                <HabboTag
                    user={received ? offer.offerer : offer.seller}
                    sub={`${received ? 'Offered' : 'You offered'} ${relativeTime(offer.createdAt)}`}
                />
                <TradeStatusPill status={offer.status} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 p-3 items-start">
                <div>
                    <SectionLabel>{received ? 'They offer' : 'You offer'}</SectionLabel>
                    <TileRow empty="Nothing.">
                        {offer.items.map((pop) => <PopTile key={pop.id} pop={pop} />)}
                    </TileRow>
                </div>
                <div className="hidden md:flex items-center self-center text-2xl text-gray-300 px-1" aria-hidden>⇄</div>
                <div>
                    <SectionLabel>{received ? 'For your' : 'For their'}</SectionLabel>
                    <TileRow empty="Listing no longer available.">
                        {(offer.listing?.items || []).map((pop) => <PopTile key={pop.id} pop={pop} />)}
                    </TileRow>
                </div>
            </div>
            {offer.note && (
                <p className="px-3 pb-2 text-sm text-gray-600 italic break-words">&ldquo;{offer.note}&rdquo;</p>
            )}
            {pending && (
                <div className="flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-t border-gray-200">
                    {received ? (
                        <>
                            <FpButton variant="ghost" size="sm" onClick={() => onDecline(offer)}>Decline</FpButton>
                            <FpButton variant="primary" size="sm" onClick={() => onAccept(offer)}>Accept trade</FpButton>
                        </>
                    ) : (
                        <FpButton variant="ghost" size="sm" onClick={() => onWithdraw(offer.id)}>Withdraw offer</FpButton>
                    )}
                </div>
            )}
        </div>
    );
}

export function TradeHistoryCard({ trade }) {
    return (
        <div className="bg-white border border-gray-300" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}>
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200 bg-gray-50">
                <HabboTag user={trade.partner} sub={`Traded ${relativeTime(trade.createdAt)}`} />
                <span className="text-[11px] text-gray-400">{trade.kind === 'offer' ? 'Via offer' : 'Direct trade'}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 p-3 items-start">
                <div>
                    <SectionLabel>You gave</SectionLabel>
                    <TileRow empty="Nothing.">{trade.gave.map((pop) => <PopTile key={pop.id} pop={pop} />)}</TileRow>
                </div>
                <div className="hidden md:flex items-center self-center text-2xl text-gray-300 px-1" aria-hidden>⇄</div>
                <div>
                    <SectionLabel>You got</SectionLabel>
                    <TileRow empty="Nothing.">{trade.got.map((pop) => <PopTile key={pop.id} pop={pop} />)}</TileRow>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pop picker - choose from your own tradeable FrankoPops
// ─────────────────────────────────────────────────────────────────────────────
const toTradePop = (p) => ({ id: p.id, serialNumber: p.serialNumber, inCase: p.inCase, setName: p.set?.name, figure: p.figure });

function useMyTradeablePops() {
    const { data, loading } = useFrankopopData(fetchMyPops, []);
    const pops = useMemo(() => (data || []).filter((p) => !p.listed).map(toTradePop), [data]);
    return { pops, loading };
}

function PopPicker({ pops, selectedIds, onToggle, max }) {
    const [search, setSearch] = useState('');
    const q = search.trim().toLowerCase();
    const filtered = q
        ? pops.filter((p) => `${p.figure?.name} ${p.setName}`.toLowerCase().includes(q))
        : pops;

    const groups = [];
    const bySet = new Map();
    for (const p of filtered) {
        const key = p.setName || 'Other';
        if (!bySet.has(key)) { bySet.set(key, []); groups.push(key); }
        bySet.get(key).push(p);
    }
    const full = selectedIds.size >= max;

    return (
        <div>
            {pops.length > 8 && (
                <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your FrankoPops..." className="mb-2" />
            )}
            <div className="max-h-[340px] overflow-y-auto border border-gray-200 bg-gray-50 p-2 space-y-3">
                {groups.length === 0 && <p className="text-sm text-gray-400 p-2">No FrankoPops match.</p>}
                {groups.map((setName) => (
                    <div key={setName}>
                        <SectionLabel>{setName}</SectionLabel>
                        <div className="flex flex-wrap gap-2">
                            {bySet.get(setName).map((p) => {
                                const selected = selectedIds.has(p.id);
                                return (
                                    <PopTile
                                        key={p.id}
                                        pop={p}
                                        selected={selected}
                                        disabled={!selected && full}
                                        onClick={() => onToggle(p.id)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{selectedIds.size} of up to {max} selected. Listed FrankoPops don&apos;t appear here.</p>
        </div>
    );
}

const toggleIn = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
};

function CostBox({ children, short }) {
    return (
        <div className={`border p-3 text-sm ${short ? 'bg-red-50 border-red-400 text-red-800' : 'bg-yellow-50 border-yellow-400 text-yellow-800'}`}>
            {children}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Post a trade
// ─────────────────────────────────────────────────────────────────────────────
const MAX_ITEMS = 10;
const PACKAGING_OPTIONS = [
    { value: 'any', label: PACKAGING_LABELS.any },
    { value: 'boxed', label: PACKAGING_LABELS.boxed },
    { value: 'unboxed', label: PACKAGING_LABELS.unboxed },
];

export function CreateListingModal({ scraps, cost, onClose, onCreated }) {
    const { pops, loading } = useMyTradeablePops();
    const [selected, setSelected] = useState(() => new Set());
    const [wants, setWants] = useState([]);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const { data: sets } = useFrankopopData(fetchSets, []);
    const [wantSetId, setWantSetId] = useState('');
    const figuresFetch = useCallback(
        () => (wantSetId ? fetchSet(wantSetId).then((r) => r.figures) : Promise.resolve([])),
        [wantSetId]
    );
    const { data: setFigures } = useFrankopopData(figuresFetch, []);
    const [wantFigureId, setWantFigureId] = useState('');
    const [wantPackaging, setWantPackaging] = useState('any');

    const short = scraps != null && scraps < cost;

    const addWant = () => {
        const figure = (setFigures || []).find((f) => f.id === wantFigureId);
        if (!figure) return;
        const setName = (sets || []).find((s) => s.id === wantSetId)?.name;
        setWants((w) => [...w, { key: `${Date.now()}-${w.length}`, figure: { ...figure, setName }, packaging: wantPackaging }]);
        setWantFigureId('');
    };

    const submit = async () => {
        setBusy(true);
        const res = await createListing({
            instanceIds: [...selected],
            wants: wants.map((w) => ({ figureId: w.figure.id, packaging: w.packaging })),
            note,
        });
        setBusy(false);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`Your trade is on the board! -${cost} ${SCRAP_UNIT}`);
        onCreated();
    };

    return (
        <FpModal title="Post a trade" onClose={onClose} maxWidth="max-w-3xl">
            <div className="space-y-5">
                <div>
                    <p className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-900 mb-1">1. What you&apos;re trading</p>
                    {loading ? (
                        <p className="text-sm text-gray-500">Loading your FrankoPops...</p>
                    ) : pops.length === 0 ? (
                        <EmptyState title="Nothing to trade" sub="You need at least one FrankoPop that isn't already listed." />
                    ) : (
                        <PopPicker pops={pops} selectedIds={selected} onToggle={(id) => setSelected((s) => toggleIn(s, id))} max={MAX_ITEMS} />
                    )}
                </div>

                <div>
                    <p className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-900 mb-1">2. What you want back</p>
                    <p className="text-xs text-gray-500 mb-2">
                        Anyone who has everything you ask for can trade straight away. Leave this empty to only take offers - people can always make an offer either way.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Set">
                            <Select value={wantSetId} onChange={(e) => { setWantSetId(e.target.value); setWantFigureId(''); }}>
                                <option value="">Choose a set...</option>
                                {(sets || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </Select>
                        </Field>
                        <Field label="FrankoPop" disabled={!wantSetId}>
                            <Select value={wantFigureId} onChange={(e) => setWantFigureId(e.target.value)} disabled={!wantSetId}>
                                <option value="">Choose a FrankoPop...</option>
                                {(setFigures || []).map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}{f.ltd ? ' (LTD)' : ''} - {f.rarityName}</option>
                                ))}
                            </Select>
                        </Field>
                    </div>
                    <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
                        <Field label="Packaging">
                            <RadioRow options={PACKAGING_OPTIONS} value={wantPackaging} onChange={setWantPackaging} />
                        </Field>
                        <FpButton variant="ghost" size="sm" disabled={!wantFigureId || wants.length >= MAX_ITEMS} onClick={addWant}>
                            Add to wants
                        </FpButton>
                    </div>
                    {wants.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {wants.map((w) => (
                                <div key={w.key} className="relative">
                                    <PopTile figure={w.figure} packaging={w.packaging} />
                                    <button
                                        type="button"
                                        onClick={() => setWants((all) => all.filter((x) => x.key !== w.key))}
                                        className="absolute top-1 right-1 bg-gray-700 text-white text-[11px] leading-none px-1.5 py-1 hover:bg-red-700"
                                        aria-label={`Remove ${w.figure.name}`}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Field label="Note (optional)">
                    <TextInput value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Looking to finish my set!" />
                </Field>

                <CostBox short={short}>
                    <p><strong>Cost:</strong> {cost} {SCRAP_UNIT} to post. Scraps are not refunded if you cancel.</p>
                    <p>{short ? `You only have ${scraps} ${SCRAP_UNIT} - not enough to post.` : `You have ${scraps ?? '-'} ${SCRAP_UNIT}.`}</p>
                </CostBox>

                <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                    <FpButton variant="secondary" onClick={onClose}>Cancel</FpButton>
                    <FpButton variant="primary" disabled={busy || short || selected.size === 0} onClick={submit}>
                        {short ? `Need ${cost} ${SCRAP_UNIT}` : `Post trade (${cost} ${SCRAP_UNIT})`}
                    </FpButton>
                </div>
            </div>
        </FpModal>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Accept a listing outright - pick which of your copies fill each want
// ─────────────────────────────────────────────────────────────────────────────
export function AcceptListingModal({ listingId, scraps, cost, onClose, onDone }) {
    const listingFetch = useCallback(() => fetchListing(listingId), [listingId]);
    const { data: listing, loading } = useFrankopopData(listingFetch, null);
    const [picks, setPicks] = useState(null);
    const [busy, setBusy] = useState(false);

    const effectivePicks = picks || Object.fromEntries((listing?.candidates || []).map((c) => [c.wantId, c.suggested || '']));
    const chosen = Object.values(effectivePicks).filter(Boolean);
    const complete = listing && chosen.length === listing.wants.length && new Set(chosen).size === chosen.length;
    const short = scraps != null && scraps < cost;

    const submit = async () => {
        setBusy(true);
        const res = await acceptListing(listingId, chosen);
        setBusy(false);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`Trade complete! -${cost} ${SCRAP_UNIT}`);
        onDone();
    };

    return (
        <FpModal title="Trade" onClose={onClose} maxWidth="max-w-2xl">
            {loading || !listing ? (
                <p className="text-sm text-gray-500">Loading...</p>
            ) : (
                <div className="space-y-4">
                    <div>
                        <SectionLabel>You get</SectionLabel>
                        <TileRow empty="Nothing.">{listing.items.map((pop) => <PopTile key={pop.id} pop={pop} />)}</TileRow>
                    </div>
                    <div>
                        <SectionLabel>You give - choose which copy</SectionLabel>
                        <div className="space-y-2">
                            {listing.wants.map((w) => {
                                const cand = (listing.candidates || []).find((c) => c.wantId === w.id);
                                const options = cand?.options || [];
                                const value = effectivePicks[w.id] || '';
                                const takenElsewhere = new Set(Object.entries(effectivePicks).filter(([k, v]) => k !== w.id && v).map(([, v]) => v));
                                return (
                                    <div key={w.id} className="flex items-center gap-3 bg-white border border-gray-200 p-2">
                                        <PopTile figure={w.figure} packaging={w.packaging} />
                                        <div className="flex-1 min-w-0">
                                            {options.length === 0 ? (
                                                <p className="text-sm text-red-600">You don&apos;t have a copy that fits this.</p>
                                            ) : (
                                                <Select value={value} onChange={(e) => setPicks({ ...effectivePicks, [w.id]: e.target.value })}>
                                                    <option value="">Choose a copy...</option>
                                                    {options.map((o) => (
                                                        <option key={o.id} value={o.id} disabled={takenElsewhere.has(o.id)}>
                                                            #{o.serialNumber} - {o.inCase ? 'In packaging' : 'Out of packaging'}
                                                        </option>
                                                    ))}
                                                </Select>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <CostBox short={short}>
                        <p><strong>Cost:</strong> {cost} {SCRAP_UNIT}. The swap happens straight away.</p>
                        <p>{short ? `You only have ${scraps} ${SCRAP_UNIT} - not enough to trade.` : `You have ${scraps ?? '-'} ${SCRAP_UNIT}.`}</p>
                    </CostBox>
                    <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                        <FpButton variant="secondary" onClick={onClose}>Cancel</FpButton>
                        <FpButton variant="primary" disabled={busy || short || !complete} onClick={submit}>
                            {short ? `Need ${cost} ${SCRAP_UNIT}` : `Trade (${cost} ${SCRAP_UNIT})`}
                        </FpButton>
                    </div>
                </div>
            )}
        </FpModal>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Make an offer from your own collection
// ─────────────────────────────────────────────────────────────────────────────
export function OfferModal({ listing, scraps, cost, onClose, onDone }) {
    const { pops, loading } = useMyTradeablePops();
    const [selected, setSelected] = useState(() => new Set());
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const short = scraps != null && scraps < cost;

    const submit = async () => {
        setBusy(true);
        const res = await makeOffer(listing.id, { instanceIds: [...selected], note });
        setBusy(false);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`Offer sent to ${listing.seller?.habboName || 'the seller'}.`);
        onDone();
    };

    return (
        <FpModal title="Make an offer" onClose={onClose} maxWidth="max-w-3xl">
            <div className="space-y-4">
                <div>
                    <SectionLabel>{listing.seller?.habboName ? `For ${listing.seller.habboName}'s` : 'For their'}</SectionLabel>
                    <TileRow empty="Nothing.">{listing.items.map((pop) => <PopTile key={pop.id} pop={pop} />)}</TileRow>
                </div>
                <div>
                    <SectionLabel>You offer</SectionLabel>
                    {loading ? (
                        <p className="text-sm text-gray-500">Loading your FrankoPops...</p>
                    ) : pops.length === 0 ? (
                        <EmptyState title="Nothing to offer" sub="You need at least one FrankoPop that isn't listed for trade." />
                    ) : (
                        <PopPicker pops={pops} selectedIds={selected} onToggle={(id) => setSelected((s) => toggleIn(s, id))} max={MAX_ITEMS} />
                    )}
                </div>
                <Field label="Note (optional)">
                    <TextInput value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Happy to add more if needed" />
                </Field>
                <CostBox short={short}>
                    <p><strong>Cost:</strong> {cost} {SCRAP_UNIT}, only taken if they accept your offer.</p>
                    <p>{short ? `You only have ${scraps} ${SCRAP_UNIT} - not enough to trade.` : `You have ${scraps ?? '-'} ${SCRAP_UNIT}.`}</p>
                </CostBox>
                <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                    <FpButton variant="secondary" onClick={onClose}>Cancel</FpButton>
                    <FpButton variant="primary" disabled={busy || short || selected.size === 0} onClick={submit}>Send offer</FpButton>
                </div>
            </div>
        </FpModal>
    );
}

// Confirm dialog used for cancelling listings and accepting/declining offers.
export function ConfirmTradeModal({ title, children, confirmLabel, variant = 'primary', onConfirm, onClose }) {
    const [busy, setBusy] = useState(false);
    return (
        <FpModal title={title} onClose={onClose} maxWidth="max-w-sm">
            <div className="text-sm text-gray-700 mb-4">{children}</div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                <FpButton variant="secondary" onClick={onClose}>Back</FpButton>
                <FpButton
                    variant={variant}
                    disabled={busy}
                    onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
                >
                    {confirmLabel}
                </FpButton>
            </div>
        </FpModal>
    );
}

export function LoginPrompt() {
    return <p className="text-sm text-gray-600">Log in to post trades, make offers and manage your listings.</p>;
}
